import { type FC, useEffect } from "react";
import { useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { FlowCard } from "../../api/dto/FlowCard";
import { CardFlowEditor } from "../../components/cardFlowEditor/CardFlowEditor";
import type { CardApi } from "../../components/cardFlowEditor/cardApi";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { Loading } from "../../components/Loading";
import { useLiftingCategoryPermissions } from "../../permissions/liftingCategoryPolicy";
import { ForbiddenPage } from "../ForbiddenPage";

// Matches Domain\LiftingCategoryCheck\LiftingCategoryCardType (gateway-api): the design the app shows
// the card in, with a standard and a result type per lifting category.
const cardTypeOptions: Record<string, string> = {
  intro: "Introductie (blauw)",
  standard_routine: "Standaard (Routine)",
  standard_niet_routine: "Standaard (Niet routine)",
  standard_kritisch: "Standaard (Kritisch)",
  action_required: "Actie vereist",
  result_routine: "Resultaat (Routine)",
  result_niet_routine: "Resultaat (Niet routine)",
  result_kritisch: "Resultaat (Kritisch)",
};

// Thin domain binding, like SettingsChecklistEditorPage: fetches the lifting category cards, gates on
// permissions, and wires the generic CardFlowEditor to the lifting-category-card endpoints.
export const SettingsLiftingCategoryEditorPage: FC = () => {
  const {
    listLiftingCategoryCards,
    addLiftingCategoryCard,
    updateLiftingCategoryCard,
    deleteLiftingCategoryCard,
    uploadLiftingCategoryCardImage,
  } = useApiCall();
  const cards = useFetchedResource<FlowCard[]>(() => listLiftingCategoryCards());
  const { loading: permissionsLoading, canManageLiftingCategoryAssistant } =
    useLiftingCategoryPermissions();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useApiCall()'s methods aren't referentially stable across renders, so cards.reload would refire every render (see SettingsChecklistsPage)
  useEffect(() => {
    cards.reload(undefined);
  }, []);

  const api: CardApi = {
    updateCard: (card) => updateLiftingCategoryCard(card),
    createCard: (name, cardType, column, positionInColumn) =>
      addLiftingCategoryCard(name, cardType, column, positionInColumn),
    deleteCard: (uuid) => deleteLiftingCategoryCard(uuid),
    uploadCardImage: (file) => uploadLiftingCategoryCardImage(file),
  };

  return (
    <Loading loading={permissionsLoading}>
      {!canManageLiftingCategoryAssistant ? (
        <ForbiddenPage />
      ) : (
        <>
          <ContentContainer size={"xl"}>
            <PageHeader>Hijscategorie assistent</PageHeader>
          </ContentContainer>
          <Loading loading={cards.loading}>
            {cards.resource ? (
              <div className={"h-[calc(100vh-12rem)] border-t border-slate-200"}>
                <CardFlowEditor
                  cards={cards.resource}
                  api={api}
                  cardTypeOptions={cardTypeOptions}
                />
              </div>
            ) : null}
          </Loading>
        </>
      )}
    </Loading>
  );
};
